import {
  TranslationDetailEntry,
  TranslationDetailSearchParameters,
  TranslationEntry,
  TranslationFileType,
  TranslationGroupEntry,
  TranslationGroupSearchParameters,
  TranslationGroupType
} from "@medapro-types/TranslationManager/translationManager";
import api from "./api";
import endpoints from "./endpoints";

export default {
  getAll: async function (): Promise<TranslationProjectEntity[]> {
    const response = await api.get(endpoints.translationManager.projects.get);
    return response.data;
  },
  getGroupCounts: async function (translationProjectId: string): Promise<TranslationGroupValueCounts> {
    const response = await api.get(endpoints.translationManager.projects.groupCounts, {
      params: {
        translationProjectId
      }
    });
    return response.data;
  },
  getValueCounts: async function (
    catalogId: string,
    sourceLanguageCode: string,
    targetLanguageCode: string,
    showOnlyOutdated: boolean
  ): Promise<TranslationGroupValueCounts> {
    const response = await api.get(endpoints.translationManager.projects.valueCounts, {
      params: {
        catalogId: catalogId,
        sourceLanguage: sourceLanguageCode,
        targetLanguage: targetLanguageCode,
        showOnlyOutdatedValues: showOnlyOutdated
      }
    });
    return response.data;
  },
  create: async function (createCriteria: TranslationProjectCreateCriteria): Promise<TranslationProjectEntity> {
    const response = await api.post(endpoints.translationManager.projects.create, createCriteria);
    return response.data;
  },
  delete: async function (translationProjectId: string): Promise<string> {
    const response = await api.post(endpoints.translationManager.projects.delete, {}, { params: { translationProjectId } });
    return response.data;
  },
  update: async function (updateModel: TranslationProjectUpdateModel): Promise<string> {
    const response = await api.post(endpoints.translationManager.projects.update, updateModel);
    return response.data;
  },
  getTranslationEntries: async function (
    searchParameters: TranslationGroupSearchParameters
  ): Promise<{ hasNextPage: boolean; entries: TranslationGroupEntry[] }> {
    const offset = (searchParameters.pageNumber - 1) * searchParameters.entriesPerPage;
    const response = await api.get(endpoints.translationManager.translationEntries.get, {
      params: {
        translationProjectId: searchParameters.translationProjectId,
        category: searchParameters.groupType,
        filterName: searchParameters.entryValueFilterType,
        searchTerm: searchParameters.searchText,
        orderByColumn: searchParameters.orderByColumn,
        orderDirection: searchParameters.orderDirection,
        offset: offset,
        pageSize: searchParameters.entriesPerPage
      }
    });

    return response.data;
  },
  getTranslationDetailEntries: async function (detailSearchParameters: TranslationDetailSearchParameters): Promise<TranslationDetailEntry[]> {
    const response = await api.get(endpoints.translationManager.translationEntries.getDetails, {
      params: {
        translationProjectId: detailSearchParameters.translationProjectId,
        category: detailSearchParameters.groupType,
        sourceText: detailSearchParameters.sourceText,
        languageCode: detailSearchParameters.languageCode
      }
    });

    return response.data;
  },
  updateTranslationEntries: async function (
    translationProjectId: string,
    translationGroup: TranslationGroupType,
    entries: TranslationEntry[]
  ): Promise<string> {
    const response = await api.post(endpoints.translationManager.translationEntries.update, entries, {
      params: {
        translationProjectId: translationProjectId,
        category: translationGroup
      }
    });

    return response.data;
  },
  translateEntriesWithDeepL: async function (translationProjectId: string, onlyEmpty: boolean, translationGroup?: TranslationGroupType): Promise<string> {
    const response = await api.post(endpoints.translationManager.translationEntries.translateWithDeepL, [], {
      params: {
        translationProjectId: translationProjectId,
        category: translationGroup,
        onlyEmpty: onlyEmpty
      }
    });

    return response.data;
  },
  exportTranslations: async function (
    fileType: TranslationFileType,
    translationProjectId: string,
    abortSignal?: AbortSignal | null,
    translationGroup?: TranslationGroupType | null
  ): Promise<string> {
    const response = await api.post(endpoints.translationManager.projects.export, [], {
      params: {
        translationProjectId: translationProjectId,
        exportType: fileType,
        categories: translationGroup
      },
      signal: abortSignal
    });

    return response.data;
  },
  importTranslations: async function (
    fileType: TranslationFileType,
    translationProjectId: string,
    file: File,
    abortSignal?: AbortSignal | null,
    translationGroup?: TranslationGroupType | null
  ): Promise<string> {
    const response = await api.post(
      endpoints.translationManager.projects.import,
      { file },
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        params: {
          translationProjectId: translationProjectId,
          translatorFileType: fileType,
          category: translationGroup
        },
        signal: abortSignal
      }
    );

    return response.data;
  },
  publishTranslationProject: async function (translationProjectId: string, abortSignal?: AbortSignal | null): Promise<string> {
    const response = await api.post(
      endpoints.translationManager.projects.publish,
      {},
      {
        params: {
          translationProjectId: translationProjectId
        },
        signal: abortSignal
      }
    );

    return response.data;
  },
  getOverviewValues: async function (translationProjectId: string): Promise<TranslationOverviewValues> {
    const response = await api.get(endpoints.translationManager.projects.overviewValues, { params: { translationProjectId: translationProjectId } });
    return response.data;
  },
  hasOverlongTargetValues: async function (translationProjectId: string): Promise<boolean> {
    const response = await api.get(endpoints.translationManager.projects.hasOverlongTargetValues, { params: { translationProjectId: translationProjectId } });
    return response.data;
  },
  updateTranslationEntryDetails: async function (
    translationProjectId: string,
    translationGroup: TranslationGroupType,
    entries: {
      referenceId: string;
      attributeId: string;
      sourceText: string;
      targetText?: string | null;
    }[]
  ): Promise<string> {
    const response = await api.post(endpoints.translationManager.translationEntries.updateDetails, entries, {
      params: {
        translationProjectId: translationProjectId,
        category: translationGroup
      }
    });

    return response.data;
  }
};

export type TranslationProjectEntity = {
  translationProjectId: string;
  name: string;
  catalogId: string;
  sourceLanguageId: number;
  targetLanguageId: number;
  updatedOn?: Date | null;
  updatedBy?: string | null;
  createdOn: Date | null;
  createdBy: string | null;
};

export type TranslationGroupValueCounts = {
  globalArticleAttributeCount: number | null;
  globalAttributeUnitCount: number | null;
  attributeValueCount: number | null;
  attributeUnitCount: number | null;
  articleNameCount: number | null;
  articleDescriptionCount: number | null;
  catalogNodeCount: number | null;
  catalogNodeDescriptionCount: number | null;
  variationGroupCount: number | null;
  catalogNodeAttributeCount: number | null;
  keywordCount: number | null;
};

export type TranslationProjectCreateCriteria = {
  name: string;
  catalogId: string;
  sourceLanguage: string;
  targetLanguage: string;
  showOnlyOutdatedValues: boolean;
  translationCategory: {
    loadCatalogGroups: boolean;
    loadAttributeValues: boolean;
    loadCatalogGroupDescriptions: boolean;
    loadAttributeUnits: boolean;
    loadCatalogGroupAttributes: boolean;
    loadGlobalAttributeValues: boolean;
    loadVariationGroups: boolean;
    loadGlobalAttributeUnits: boolean;
    loadArticleNames: boolean;
    loadKeywords: boolean;
    loadDescriptions: boolean;
  };
};

export type TranslationProjectUpdateModel = {
  translationProjectId: string;
  name: string;
};

export type TranslationOverviewValues = {
  textCount: number | null;
  emptyTextCount: number | null;
  translatedTextCount: number | null;
  wordCount: number | null;
  emptyWordCount: number | null;
  translatedWordCount: number | null;
  characterCount: number | null;
  emptyCharacterCount: number | null;
  translatedCharacterCount: number | null;
};
